import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  TextField,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Edit, Save } from "@mui/icons-material";
import styles from "./ManageAccess.module.scss";
import CustomDialog from "../../../library/common/CustomDialog";
import SearchBar from "../../../library/common/SearchBar/SearchBar";
import { CONSTANTS } from "../../../library/common/constant";
import { api } from "../../../services";
import { throttle } from "lodash";

const {
  MANAGE_ACCESS_TITLE,
  ADD_NEW_USER_BUTTON,
  MANAGE_ACCESS_SEARCH_LABEL,
  ADD_BUTTON,
  CANCEL_BUTTON,
  DELETE_BUTTON,
  DELETE_CONFIRM_TITLE,
  DELETE_CONFIRM_MESSAGE,
  THROTTLE_DURATION,
} = CONSTANTS;

const ManageAccess = () => {
  const users = useRef([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "viewer",
  });
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const filterUsers = useCallback(() => {
    if (users.current?.length !== 0) {
      setFilteredUsers(
        users.current?.filter(
          (item) =>
            item?.name?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
            item?.email?.toLowerCase()?.includes(searchText?.toLowerCase())
        )
      );
    }
  }, [searchText]);

  const getUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      users.current = data;
      filterUsers();
    } catch (error) {
      console.error("Falied to fetch users", error);
    }
  }, [filterUsers]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const handleRoleChange = (index, newRole) => {
    const updatedUsers = [...users.current];
    if (updatedUsers?.[index]) {
      updatedUsers[index].role = newRole;
      users.current = updatedUsers;
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setUserToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteUser = throttle(async (uid) => {
    try {
      setIsLoading(true);
      await api.delete(`/users/` + uid);
      users.current = users.current?.filter((user) => user?.uid !== uid);
      filterUsers();
      closeDeleteDialog();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleAddButtonClick = () => {
    setModalOpen(true);
    setErrors({});
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setNewUser({ name: "", email: "", role: "viewer" });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newUser?.name?.trim()) newErrors.name = "Name is required.";
    if (!newUser?.email?.trim()) newErrors.email = "Email is required.";
    if (!newUser?.password?.trim())
      newErrors.password = "Password is required.";
    return newErrors;
  };

  const handleAddUser = throttle(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        setIsLoading(true);
        await api.post("/users", newUser);
        await getUsers();
        handleCloseModal();
      } catch (error) {
        console.error("Failed to post: ", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, THROTTLE_DURATION);

  const handleEditClick = (id) => {
    const user = filteredUsers?.find((item) => item?._id === id);
    setEditingId(id);
    setEditedUser(user);
  };

  const handleSaveClick = throttle(async () => {
    try {
      setIsLoading(true);
      await api.patch(`/users/${editingId}`, {
        name: editedUser?.name,
        email: editedUser?.email,
        role: editedUser?.role,
      });
      users.current = users.current?.map((user) =>
        user?._id === editingId ? { ...user, ...editedUser } : user
      );
      setEditingId(null);
    } catch (error) {
      console.error("Failed to update: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEditedInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <Box className={styles.manageAccessContainer}>
      {/* Heading */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" className={styles.manageAccessHeading}>
          {MANAGE_ACCESS_TITLE}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddButtonClick}
        >
          {ADD_NEW_USER_BUTTON}
        </Button>
      </Box>

      {/* Search Bar */}
      <div className={styles.manageAccessSearchBar}>
        <SearchBar
          placeholderText={MANAGE_ACCESS_SEARCH_LABEL}
          updateSearchText={handleSearch}
        />
      </div>

      {/* User Cards */}
      {filteredUsers?.map((user, index) => {
        const { _id, name, email, role } = user || {};

        return (
          <Card key={index} className={styles.manageAccessCard}>
            <CardContent className={styles.manageAccessCardContent}>
              <Box>
                {editingId === _id ? (
                  <>
                    <TextField
                      margin="dense"
                      name="name"
                      value={editedUser?.name || ""}
                      onChange={handleEditedInputChange}
                    />
                    <TextField
                      margin="dense"
                      name="email"
                      value={editedUser?.email ?? ""}
                      onChange={handleEditedInputChange}
                    />
                  </>
                ) : (
                  <>
                    <Typography variant="h6">{name}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      {email}
                    </Typography>
                  </>
                )}
              </Box>
            </CardContent>

            {/* Role Selector, Edit, and Delete Buttons */}
            <Box className={styles.manageAccessActions}>
              {editingId === _id ? (
                <IconButton
                  color="primary"
                  onClick={handleSaveClick}
                  disabled={isLoading}
                >
                  <Save />
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  onClick={() => handleEditClick(_id)}
                >
                  <Edit />
                </IconButton>
              )}
              <Select
                value={editingId === _id ? editedUser?.role : role}
                className={styles.manageAccessSelect}
                size="small"
                onChange={(e) => {
                  if (editingId === _id) {
                    setEditedUser((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }));
                  } else {
                    handleRoleChange(index, e.target.value);
                  }
                }}
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="collaborator">Collaborator</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super admin">Super Admin</MenuItem>
              </Select>

              <Button
                variant="contained"
                color="error"
                size="small"
                className={styles.manageAccessDeleteBtn}
                onClick={() => openDeleteDialog(user)}
              >
                {DELETE_BUTTON}
              </Button>
            </Box>
          </Card>
        );
      })}

      {/* Add User Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>{ADD_NEW_USER_BUTTON}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Name"
            name="name"
            value={newUser?.name ?? ""}
            onChange={handleInputChange}
            error={!!errors?.name}
            helperText={errors?.name}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Password"
            name="password"
            value={newUser?.password ?? ""}
            onChange={handleInputChange}
            error={!!errors?.password}
            helperText={errors?.password}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Email"
            name="email"
            value={newUser?.email ?? ""}
            onChange={handleInputChange}
            error={!!errors?.email}
            helperText={errors?.email}
          />
          <Select
            fullWidth
            margin="dense"
            name="role"
            value={newUser?.role}
            onChange={handleInputChange}
          >
            <MenuItem value="viewer">Viewer</MenuItem>
            <MenuItem value="collaborator">Collaborator</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="super admin">Super Admin</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{CANCEL_BUTTON}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddUser}
            disabled={isLoading}
          >
            {ADD_BUTTON}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomDialog
        open={isDeleteDialogOpen}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={closeDeleteDialog}
        onPrimaryClick={() => handleDeleteUser(userToDelete?.uid)}
        onSecondaryClick={closeDeleteDialog}
        disablePrimary={isLoading}
      />
    </Box>
  );
};

export default ManageAccess;
